import * as yup from 'yup';
import { Row, RenderConfig, TextInput, CASE, Column, EMPTY_ARRAY } from 'commons-ui';

export const FORM_FIELD_KEYS = {
	CONFIGS: 'configs',
	DEVICE_TYPE: 'deviceType',
	MIN_APP_VERSION: 'minAppVersion',
	MAX_APP_VERSION: 'maxAppVersion',
	URL: 'url',
	BACKGROUND_COLOR: 'backgroundColor'
};

export const validationSchema = yup.object().shape({
	configs: yup.array().of(
		yup.object().shape({
			deviceType: yup
				.string()
				.required('Device Type is a required field')
				.oneOf(['IOS', 'ANDROID'], 'Device Type must be either IOS or ANDROID'),
			minAppVersion: yup
				.string()
				.required('Min App Version is a required field')
				.test('min-version-check', 'Invalid min version', function validateMinVersion(value) {
					const { deviceType } = this.parent;
					if (!value) return false;
					const numValue = parseInt(value, 10);
					return deviceType === 'IOS' ? numValue >= 6017 : numValue >= 0;
				})
				.test('first-range-check', 'First range must start from 6017 for IOS', function validateFirstRange() {
					const configs = this.from && this.from[1] && this.from[1].value.configs;
					if (!configs || !Array.isArray(configs)) return true;
					const currentDeviceType = this.parent.deviceType;
					if (currentDeviceType !== 'IOS') return true;

					const deviceConfigs = configs.filter((config) => config.deviceType === currentDeviceType);
					if (deviceConfigs.length === 0) return true;

					// Sort ranges by minAppVersion
					const sortedRanges = deviceConfigs
						.map((config) => ({
							min: parseInt(config.minAppVersion, 10),
							max: parseInt(config.maxAppVersion, 10)
						}))
						.sort((a, b) => a.min - b.min);

					// Check if the first range starts from the minimum allowed version
					return sortedRanges[0].min === 6017;
				})
				.test('first-range-check', 'First range must start from 0 for Anndroif', function validateFirstRange() {
					const configs = this.from && this.from[1] && this.from[1].value.configs;
					if (!configs || !Array.isArray(configs)) return true;
					const currentDeviceType = this.parent.deviceType;
					if (currentDeviceType !== 'ANDROID') return true;

					const deviceConfigs = configs.filter((config) => config.deviceType === currentDeviceType);
					if (deviceConfigs.length === 0) return true;

					// Sort ranges by minAppVersion
					const sortedRanges = deviceConfigs
						.map((config) => ({
							min: parseInt(config.minAppVersion, 10),
							max: parseInt(config.maxAppVersion, 10)
						}))
						.sort((a, b) => a.min - b.min);

					// Check if the first range starts from the minimum allowed version
					return sortedRanges[0].min === 0;
				})
				.test('continuous-range-check', 'Version ranges must be continuous', function validateContinuousRange() {
					const configs = this.from && this.from[1] && this.from[1].value.configs;
					if (!configs || !Array.isArray(configs)) return true;
					const currentDeviceType = this.parent.deviceType;
					const deviceConfigs = configs.filter((config) => config.deviceType === currentDeviceType);
					// Sort ranges by minAppVersion
					const sortedRanges = deviceConfigs
						.map((config) => ({
							min: parseInt(config.minAppVersion, 10),
							max: parseInt(config.maxAppVersion, 10)
						}))
						.sort((a, b) => a.min - b.min);
					// Check for gaps between ranges
					for (let i = 0; i < sortedRanges.length - 1; i += 1) {
						if (sortedRanges[i].max + 1 !== sortedRanges[i + 1].min) {
							return false;
						}
					}
					return true;
				}),
			maxAppVersion: yup
				.string()
				.required('Max App Version is a required field')
				.test('max-version-check', 'Invalid max version', function validateMaxVersion(value) {
					if (!value) return false;
					const numValue = parseInt(value, 10);
					return numValue <= 10000;
				}),
			url: yup.string().required('Url is a required field'),
			backgroundColor: yup.string().required('Background Color is a required field')
		})
	)
});

export const FORM_FIELD_CONFIG = {
	[FORM_FIELD_KEYS.CONFIGS]: new RenderConfig()
		.setName('configs')
		.setLabel('Configurations')
		.setRenderProps({ isEditable: { create: true, edit: true }, isArrayField: true, required: true }),
	[FORM_FIELD_KEYS.DEVICE_TYPE]: new RenderConfig()
		.setName('deviceType')
		.setLabel('Device Type')
		.setRenderer(TextInput)
		.setRenderProps({ isEditable: { create: true, edit: true }, letterCase: CASE.UPPERCASE, required: true }),
	[FORM_FIELD_KEYS.MIN_APP_VERSION]: new RenderConfig()
		.setName('minAppVersion')
		.setLabel('Min App Version')
		.setRenderer(TextInput)
		.setRenderProps({ isEditable: { create: true, edit: true }, required: true }),
	[FORM_FIELD_KEYS.MAX_APP_VERSION]: new RenderConfig()
		.setName('maxAppVersion')
		.setLabel('Max App Version')
		.setRenderer(TextInput)
		.setRenderProps({ isEditable: { create: true, edit: true }, required: true }),
	[FORM_FIELD_KEYS.URL]: new RenderConfig()
		.setName('url')
		.setLabel('URL')
		.setRenderer(TextInput)
		.setRenderProps({ isEditable: { create: true, edit: true }, required: true }),
	[FORM_FIELD_KEYS.BACKGROUND_COLOR]: new RenderConfig()
		.setName('backgroundColor')
		.setLabel('Background Color')
		.setRenderer(TextInput)
		.setRenderProps({ isEditable: { create: true, edit: true }, required: true })
};

export const FORM_LAYOUT_CONFIG = [
	new Row(FORM_FIELD_KEYS.CONFIGS, EMPTY_ARRAY, [
		new Column([new Row(FORM_FIELD_KEYS.DEVICE_TYPE)]),
		new Column([new Row(FORM_FIELD_KEYS.MIN_APP_VERSION)]),
		new Column([new Row(FORM_FIELD_KEYS.MAX_APP_VERSION)]),
		new Column([new Row(FORM_FIELD_KEYS.URL)]),
		new Column([new Row(FORM_FIELD_KEYS.BACKGROUND_COLOR)])
	])
];
